<template>
  <b-collapse :id="collapseId" class="search-collapse">
    <div id="search">
      <div class="search-header">
        <div class="search-header__inner">
          <div class="search-header__inner__input">
            <BaseSearchInput
              ref="baseSearch"
              :loading.sync="loading"
              :search-term.sync="searchTerm"
              @on-search="setSearchParams"
              @on-reset="clearSearchResults"
            />
            <span class="focus-border" />
          </div>
          <div class="search-header__inner__tab-buttons">
            <ul>
              <li v-for="button in resultCategories" :key="button.id">
                <button
                  type="button"
                  :class="{ active: selectedTab === button }"
                  @click="selectedTab = button"
                >
                  {{ button }}
                </button>
              </li>
            </ul>
            <span v-if="searchResultsCount > 0">{{ resultsString }}</span>
            <span v-if="noResults">Nothing found!</span>
          </div>
        </div>
      </div>
      <div class="search-container">
        <div class="search">
          <div class="search__body">
            <div class="search__body__grid">
              <div class="results">
                <div
                  v-for="(result, key, index) in searchResultsWithFilter"
                  :key="`#${key}-${index}-key`"
                  class="results__row"
                >
                  <template v-if="result">
                    <div class="results__row__heading">
                      <h2>{{ key }}</h2>
                      <button v-if="result.length === 4" @click="onEntitySelect(key)">
                        {{ checkLength(key) }}
                      </button>
                    </div>
                    <div v-if="result && key === 'locations'" class="results__row__content">
                      <SearchLocation :locations="result" />
                    </div>
                    <div v-if="result && key === 'artists'" class="results__row__content">
                      <ArtistCard
                        v-for="artist in result"
                        :key="artist.id"
                        :artist-data="artist"
                        :artist-type="artist.artistType"
                      >
                        <template #artist-description>
                          <!-- eslint-disable vue/no-v-html -->
                          <p v-html="artist.bio" />
                        </template>
                      </ArtistCard>
                    </div>
                    <div v-if="result && key === 'episodes'" class="results__row__content">
                      <EpisodeCard
                        v-for="episode in result"
                        :key="episode.id"
                        :episode="episode"
                        :artist="episode.mainArtist"
                        :artist-name="true"
                      >
                        <template #episode-description>
                          <p v-html="episode.info" />
                        </template>
                      </EpisodeCard>
                    </div>
                    <div v-if="result && key === 'shows'" class="results__row__content">
                      <ShowCard v-for="show in result"
                                :key="show.id"
                                :show-data="show"
                      >
                        <template #show-description>
                          <p v-html="show.description" />
                        </template>
                      </ShowCard>
                    </div>
                    <div v-if="result && key === 'tracks'" class="results__row__content">
                      <SearchTracklist :tracklist="result" />
                    </div>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <SearchNotFound v-if="noResults" />
    </div>
  </b-collapse>
</template>
<script>
import axios from 'axios';
import BaseSearchInput from '@/components/search/BaseSearchInput.vue';
import ArtistCard from '@/components/cards/ArtistCard.vue';
import EpisodeCard from '@/components/cards/EpisodeCard.vue';
import ShowCard from '@/components/cards/ShowCard.vue';
import SearchNotFound from '@/components/svg/SearchNotFound.vue';
import SearchTracklist from '@/components/search/SearchTracklist.vue';
import SearchLocation from '@/components/search/SearchLocation.vue';

export default {
  name: 'Search',
  components: {
    BaseSearchInput,
    ArtistCard,
    EpisodeCard,
    ShowCard,
    SearchNotFound,
    SearchTracklist,
    SearchLocation
  },
  props: {
    collapseId: {
      type: String,
      required: true
    },
    searchCollapseState: {
      type: Boolean,
      required: true
    }
  },
  data: () => ({
    loading: false,
    searchResults: {},
    searchTerm: '',
    selectedTab: 'all',
    urlHadSearchParams: false
  }),
  computed: {
    resultsString() {
      return `${this.searchResultsCount} results`;
    },
    noResults(){
      return this.hasResults && this.resultsAreEmpty;
    },
    hasResults() {
      return (
        Object.values(this.searchResults).every(e => e == null) &&
        !this.loading
      );
    },
    resultsAreEmpty(){
      return Object.entries(this.searchResults).length > 0;
    },
    searchResultsCount() {
      const results = this.searchResults;
      return Object.values(results)
        .filter(e => e != null)
        .reduce((previous, current) => current.length + previous, 0);
    },
    resultCategories() {
      const results = this.searchResults;
      const keys = Object.keys(
        Object.fromEntries(Object.entries(results).filter(e => e[1] != null))
      );
      keys.length ? keys.unshift('all') : keys;
      return keys;
    },
    searchResultsWithFilter() {
      const allResults = this.setLimitedResultItems(this.searchResults);
      const filteredResults = Object.keys(this.searchResults)
        .filter(key => this.selectedTab.includes(key))
        .reduce((obj, key) => {
          obj[key] = this.searchResults[key];
          return obj;
        }, {});
      return this.selectedTab === 'all' ? allResults : filteredResults;
    }
  },
  watch: {
    searchCollapseState(state) {
      if (state && !this.urlHadSearchParams) {
        this.$router.replace({query: {s: this.searchTerm}});
        setTimeout(() => { // after animation is finished
          this.$refs.baseSearch.inputFocus();
        }, 500);
      } else if (!state) {
        if (this.$route.query.hasOwnProperty('s')) this.$router.replace({query: null}); // remove search params
        this.urlHadSearchParams = false;
      }
    },
    '$route.query'(newRoute) {
      const hasRouteSearchParam = newRoute.hasOwnProperty('s');
      if (hasRouteSearchParam) {
        this.urlHadSearchParams = true;
        // eslint-disable-next-line vue/custom-event-name-casing
        if (!this.searchCollapseState) this.$root.$emit('bv::toggle::collapse', this.collapseId); // Trigger open search
        this.getSearch(newRoute.s);
        this.searchTerm = newRoute.s;
      } else {
        // eslint-disable-next-line vue/custom-event-name-casing
        if (this.searchCollapseState) this.$root.$emit('bv::toggle::collapse', this.collapseId); // Trigger close search
      }
    },
    selectedTab() {
      setTimeout(() => {
        this.$el.scrollTo({
          top: 0,
          left: 0,
          behavior: 'smooth'
        });
      }, 250);
    }
  },
  methods: {
    setSearchParams(searchTerm) {
      this.searchTerm = searchTerm;
      this.$router.push({query: {s: searchTerm}});
    },
    checkLength(key) {
      return this.searchResults[key].length > 4 ? 'SHOW ALL' : '';
    },
    setLimitedResultItems(results) {
      return Object.fromEntries(Object.entries(results).map(key => {
        if (key[1] != null && key[1].length > 4) {
          let newArray = key[1];
          key[1] = newArray.slice(0, 4);
        }
        return key;
      }));
    },
    onEntitySelect(key) {
      this.selectedTab = key;
    },
    getSearch(searchTerm) {
      axios.get(this.$router.options.api + '/search/name?search=' + searchTerm).then(response => {
        if (Object.keys(response.data).length) {
          this.searchResults = {
            locations: response.data.locations,
            episodes: response.data.episodes,
            artists: response.data.artists,
            shows: response.data.shows,
            tracks: response.data.tracklist
          };
          this.setMarkOnMatchWord(this.searchResults);
        }
        this.loading = false;
        this.$refs.baseSearch.$refs.mvmntSearch.blur(); // collapse keyboard on mobile
      });
    },
    clearSearchResults() {
      this.searchResults = {};
      this.loading = false;
      this.searchTerm = '';
    },
    setMarkOnMatchWord(object){
      for (let key of Object.entries(object)){
        if (key[0] === 'episodes' && key[1] != null) {
            key[1].forEach(e => e.info = this.setFoundWord(e.info));
        }
        if (key[0] === 'artists' && key[1] != null) {
            key[1].forEach(e => e.bio = this.setFoundWord(e.bio));
        }
        if (key[0] === 'shows' && key[1] != null) {
            key[1].forEach(e => e.description = this.setFoundWord(e.description));
        }
      }
    },
    setFoundWord(string) {
      const regex = new RegExp(`${this.searchTerm}`, 'gi');
      if (string != null) {
        return string.replace(regex, `<strong>$&</strong>`);
      }
    }
  }
};
</script>
<style scoped lang="scss">
@import '@/assets/sass/components/search/_app-search-collapse.scss';
</style>
